// File Block — URL or upload; preview uses file-type styling
import { escapeAttr, performMediaUpload, canUploadMedia, formatFileSize, fileExtensionFrom, editorFileIconStyles } from './blockUtils';

class FileBlock {
  static get toolbox() {
    return {
      title: 'File',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, readOnly }) {
    this.data = {
      url: data.url || '',
      filename: data.filename || data.name || 'Document',
      size: data.size || '',
      mime: data.mime || ''
    };
    this.readOnly = readOnly;
    this.wrapper = null;
    this.mediaUpload = (config && config.mediaUpload) || {};
    this._uploading = false;
  }

  render() {
    this.wrapper = document.createElement('div');
    this._render();
    return this.wrapper;
  }

  _spaceId() {
    return this.mediaUpload.spaceId || '';
  }

  _iconWrapClasses(filename, url) {
    const ext = fileExtensionFrom(filename || url);
    return editorFileIconStyles(ext);
  }

  _updatePreview() {
    const preview = this.wrapper.querySelector('.file-preview');
    const nameDisplay = this.wrapper.querySelector('.file-name-display');
    const sizeDisplay = this.wrapper.querySelector('.file-size-display');
    const iconWrap = this.wrapper.querySelector('.file-icon-wrap');

    if (this.data.url) {
      if (preview) preview.classList.remove('hidden');
      if (nameDisplay) nameDisplay.textContent = this.data.filename || 'Document';
      if (sizeDisplay) {
        if (this.data.size) {
          sizeDisplay.textContent = this.data.size;
          sizeDisplay.classList.remove('hidden');
        } else {
          sizeDisplay.classList.add('hidden');
        }
      }
      const link = this.wrapper.querySelector('.file-link');
      if (link) link.href = this.data.url;
      if (iconWrap) {
        const { box, stroke } = this._iconWrapClasses(this.data.filename, this.data.url);
        iconWrap.className = `file-icon-wrap p-2 rounded ${box}`;
        const svg = iconWrap.querySelector('svg');
        if (svg) svg.setAttribute('class', `w-8 h-8 ${stroke}`);
      }
    } else if (preview) {
      preview.classList.add('hidden');
    }
  }

  _render() {
    const eu = escapeAttr;
    const { box, stroke } = this._iconWrapClasses(this.data.filename, this.data.url);

    this.wrapper.innerHTML = `
      <div data-testid="file-block">
        ${!this.readOnly ? `
          <div class="flex gap-2 mb-2 flex-wrap items-center">
            <input type="text" class="url-input flex-1 min-w-48 px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900" placeholder="File URL..." value="${eu(this.data.url)}" />
            <input type="file" class="file-file-input hidden" />
            <button type="button" class="file-upload-btn px-3 py-2 text-sm rounded border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap" ${this._uploading ? 'disabled' : ''}>
              ${this._uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input type="text" class="name-input px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-sm w-40 bg-white dark:bg-slate-900" placeholder="Filename" value="${eu(this.data.filename)}" />
            <input type="text" class="size-input px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-sm w-28 bg-white dark:bg-slate-900" placeholder="Size" value="${eu(this.data.size)}" title="Optional; auto-filled on upload" />
          </div>
          <p class="file-upload-err text-xs text-red-600 mb-2 hidden"></p>
        ` : ''}
        <a href="${eu(this.data.url || '#')}" target="_blank" rel="noopener" class="file-link file-preview flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${!this.data.url ? 'hidden' : ''}">
          <div class="file-icon-wrap p-2 rounded ${box}">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-8 h-8 ${stroke}"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="file-name-display font-medium text-slate-900 dark:text-white truncate">${eu(this.data.filename)}</div>
            <div class="file-size-display text-sm text-slate-500 ${!this.data.size ? 'hidden' : ''}">${eu(this.data.size)}</div>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-slate-400 shrink-0"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
        </a>
      </div>
    `;

    if (!this.readOnly) {
      const errEl = this.wrapper.querySelector('.file-upload-err');
      const showErr = (msg) => {
        if (!errEl) return;
        errEl.textContent = msg || '';
        errEl.classList.toggle('hidden', !msg);
      };

      const urlInput = this.wrapper.querySelector('.url-input');
      const nameInput = this.wrapper.querySelector('.name-input');
      const sizeInput = this.wrapper.querySelector('.size-input');
      const fileInput = this.wrapper.querySelector('.file-file-input');
      const uploadBtn = this.wrapper.querySelector('.file-upload-btn');

      urlInput?.addEventListener('input', (e) => {
        this.data.url = e.target.value;
        showErr('');
        this._updatePreview();
      });

      nameInput?.addEventListener('input', (e) => {
        this.data.filename = e.target.value;
        this._updatePreview();
      });

      sizeInput?.addEventListener('input', (e) => {
        this.data.size = e.target.value;
        this._updatePreview();
      });

      uploadBtn?.addEventListener('click', () => {
        showErr('');
        if (!canUploadMedia(this.mediaUpload)) {
          showErr('Uploads are not available in this context.');
          return;
        }
        fileInput?.click();
      });

      fileInput?.addEventListener('change', async () => {
        const file = fileInput.files && fileInput.files[0];
        fileInput.value = '';
        if (!file) return;
        this._uploading = true;
        this._render();
        try {
          const res = await performMediaUpload(this.mediaUpload, file, 'file');
          this.data.url = res.url || '';
          this.data.filename = res.filename || file.name || this.data.filename;
          this.data.mime = res.mime || '';
          this.data.size = res.size != null ? formatFileSize(res.size) : this.data.size;
          showErr('');
        } catch (e) {
          showErr(e.message || 'Upload failed');
        } finally {
          this._uploading = false;
          this._render();
        }
      });
    }
  }

  save() {
    return {
      url: this.data.url,
      filename: this.data.filename,
      size: this.data.size,
      mime: this.data.mime || undefined
    };
  }
}

export default FileBlock;
